import { clsx } from 'clsx';
import { ProductsType } from 'src/constants/products';
import { AgentCoSupportIcon } from 'src/assets/icons/agentCoSupportIcon';
import { CustomerCoSupportIcon } from 'src/assets/icons/customerCoSupportIcon';
import { BiCoSupportIcon } from 'src/assets/icons/biCoSupportIcon';
import { SettingsPopover } from 'src/components/HomePageComponents/SettingPopover';
import { ProjectStatusBadge } from 'src/pages/ControlDeskPage/ProjectTitle/ProjectStatusBadge';
import { Separator } from 'src/components/ui/separator';
import { Link } from 'react-router-dom';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { LATEST_VIEWED_PROJECT_ID } from 'src/shared/constants';
import { setProjectId } from 'src/store/slices/projectsSlices';
import { useAppDispatch } from 'src/store';
import { getIntegrationCatalog } from 'src/components/Integrations/IntegrationTypes';
import type { Project, ProjectStatistics } from 'src/services/projectsService';
import type { ProductKey } from 'src/constants/products';

const PRODUCT_HEADER: Partial<Record<ProductKey, { icon: JSX.Element; title: string }>> = {
  [ProductsType.agent]: {
    icon: <AgentCoSupportIcon width="19" height="19" />,
    title: 'AI Assistant',
  },
  [ProductsType.customer]: {
    icon: <CustomerCoSupportIcon width="19" height="19" />,
    title: 'AI Agent',
  },
  [ProductsType.bi]: {
    icon: <BiCoSupportIcon width="19" height="19" />,
    title: 'Business Intelligence',
  },
};

// Change indicators for the commented-out accuracy and resolution diffs below.
// const ChevronUp = () => (
//   <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path d="M4.5 1L8.39711 7.75L0.602886 7.75L4.5 1Z" fill="#389D61" />
//   </svg>
// );
// const ChevronDown = () => (
//   <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path d="M4.5 9L0.602887 2.25L8.39711 2.25L4.5 9Z" fill="#C11A45" />
//   </svg>
// );

export const ProductCard = ({
  project,
  isFetchingStat,
}: {
  project: Project & ProjectStatistics;
  isFetchingStat: boolean;
}) => {
  const dispatch = useAppDispatch();

  const changeProject = () => {
    localStorage.setItem(LATEST_VIEWED_PROJECT_ID, String(project?.id));
    dispatch(setProjectId({ projectId: project?.id, projectType: project?.project_type }));
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const integrationTypes = getIntegrationCatalog(project?.project_type);

  const isIntegrationEmpty = !project?.integrations?.length;
  const header = PRODUCT_HEADER[project?.project_type];

  return (
    <Link
      onClick={() => changeProject()}
      to={
        project.project_type === ProductsType.customer
          ? `${PATH_DASHBOARD.PROJECTS}/${project.id}/control-desk?type=${project.project_type}`
          : `${PATH_DASHBOARD.PROJECTS}/${project.id}?type=${project.project_type}`
      }
    >
      <div
        className={clsx(
          'border-light flex flex-col relative border hover:border-[#05050d33] shadow rounded-sm-5 shadow-[#00000026] hover:shadow-[0_3px_4px_0_#00000040] transition-all h-full',
        )}
      >
        <div className="flex justify-between w-full p-2.5 border-b">
          <div className="flex gap-2 items-center">
            {header?.icon}
            <p className="text-base-xs font-jamjuree font-medium"> {header?.title}</p>
          </div>
          <div>
            <SettingsPopover project={project} />
          </div>
        </div>
        <div className="flex justify-between w-full p-5 pt-7.5 pb-[60px]">
          <div className="flex items-start gap-[10px] truncate flex-col">
            <div className="flex flex-col gap-1 w-full">
              <p
                title={project?.name}
                className="font-bold text-start font-jamjuree text-lg text-ebony opacity-80 truncate w-full"
              >
                {project?.name}
              </p>
              {!isIntegrationEmpty &&
                project.integrations.map((integration) => {
                  const type = integration.integration_type;
                  const integrationTypeConfig = (integrationTypes || []).find(
                    (integrationType) => integrationType.data.integration_type === type,
                  );
                  return (
                    <p
                      key={integration.id}
                      title=""
                      className="font-medium text-start font-jamjuree text-sm text-ebony opacity-80 truncate w-full"
                    >
                      {integrationTypeConfig?.friendlyName || ''}
                    </p>
                  );
                })}
            </div>
            <ProjectStatusBadge project={project} />
          </div>
        </div>
        <div className="relative mt-auto">
          <div className="z-50 bg-white ml-[15px] px-[5px] w-fit">
            <p className="opacity-25 font-jamjuree font-medium text-tiny w-fit">Monthly Insight</p>
          </div>
          <Separator
            className={'bg-[#E3E6E8] absolute top-1/2 transform -translate-y-1/2 w-full -z-10'}
          />
        </div>
        <div className="relative pt-2 pb-[15px] px-5">
          <div className="flex gap-7.5">
            <div className="flex flex-col gap-0.5 justify-between">
              <p className="opacity-80 font-jamjuree font-medium text-base-xs">
                {project?.project_type === ProductsType.agent && 'Accuracy'}
                {project?.project_type === ProductsType.customer && 'Resolution rate'}
                {project?.project_type === ProductsType.bi && 'Resolution rate'}
              </p>
              <div className={clsx(isFetchingStat && 'animate-pulse blur-sm', 'flex gap-1')}>
                {project?.project_type === ProductsType.agent &&
                  (project?.accuracy ? (
                    <div className="flex items-center gap-1">
                      <div className="flex items-center">
                        <p className={'font-jamjuree font-bold text-lg w-fit'}>
                          {project?.accuracy}%
                        </p>
                      </div>
                      {/*<div className="flex items-center">*/}
                      {/*  {project?.accuracy_diff > 0 && <ChevronUp />}*/}
                      {/*  {project?.accuracy_diff < 0 && <ChevronDown />}*/}
                      {/*  <p*/}
                      {/*    className={clsx(*/}
                      {/*      project?.accuracy_diff > 0 && 'text-[#389D61]',*/}
                      {/*      project?.accuracy_diff < 0 && 'text-[#C11A45]',*/}
                      {/*      'font-jamjuree font-semibold text-xs w-fit',*/}
                      {/*    )}*/}
                      {/*  >*/}
                      {/*    {project?.accuracy_diff}%*/}
                      {/*  </p>*/}
                      {/*</div>*/}
                    </div>
                  ) : (
                    <p className="font-jamjuree font-bold text-lg w-fit">0%</p>
                  ))}
                {project?.project_type === ProductsType.customer &&
                  (project?.resolution_rate ? (
                    <div className="flex items-center gap-1">
                      <div className="flex items-center">
                        <p className={'font-jamjuree font-bold text-lg w-fit'}>
                          {project?.resolution_rate}%
                        </p>
                      </div>
                      {/*<div className="flex items-center">*/}
                      {/*  {project?.resolution_rate_diff > 0 && <ChevronUp />}*/}
                      {/*  {project?.resolution_rate_diff < 0 && <ChevronDown />}*/}
                      {/*  <p*/}
                      {/*    className={clsx(*/}
                      {/*      project?.resolution_rate_diff > 0 && 'text-[#389D61]',*/}
                      {/*      project?.resolution_rate_diff < 0 && 'text-[#C11A45]',*/}
                      {/*      'font-jamjuree font-semibold text-xs w-fit',*/}
                      {/*    )}*/}
                      {/*  >*/}
                      {/*    {project?.resolution_rate_diff}%*/}
                      {/*  </p>*/}
                      {/*</div>*/}
                    </div>
                  ) : (
                    <p className="font-jamjuree font-bold text-lg w-fit">0%</p>
                  ))}
                {project?.project_type === ProductsType.bi && (
                  <p className="font-jamjuree font-bold text-lg w-fit">0%</p>
                )}
              </div>
            </div>
            <div className="flex flex-col justify-between">
              <p className="opacity-80 font-jamjuree font-medium text-base-xs">
                {project?.project_type === ProductsType.agent && 'Tickets resolved with AI'}
                {project?.project_type === ProductsType.customer && 'Processed tickets'}
                {project?.project_type === ProductsType.bi && 'Processed tickets'}
              </p>
              <div className={clsx(isFetchingStat && 'animate-pulse blur-sm', 'flex gap-1')}>
                {project?.project_type === ProductsType.agent &&
                  (project?.tickets_resolved_with_ai ? (
                    <div className="flex items-center gap-1">
                      <div className="flex items-center">
                        <p className={'font-jamjuree font-bold text-lg w-fit'}>
                          {project?.tickets_resolved_with_ai}
                        </p>
                      </div>
                      {/*<div className="flex items-center">*/}
                      {/*  {project?.tickets_resolved_with_ai_diff > 0 && <ChevronUp />}*/}
                      {/*  {project?.tickets_resolved_with_ai_diff < 0 && <ChevronDown />}*/}
                      {/*  <p*/}
                      {/*    className={clsx(*/}
                      {/*      project?.tickets_resolved_with_ai_diff > 0 && 'text-[#389D61]',*/}
                      {/*      project?.tickets_resolved_with_ai_diff < 0 && 'text-[#C11A45]',*/}
                      {/*      'font-jamjuree font-semibold text-xs w-fit',*/}
                      {/*    )}*/}
                      {/*  >*/}
                      {/*    {project?.tickets_resolved_with_ai_diff}%*/}
                      {/*  </p>*/}
                      {/*</div>*/}
                    </div>
                  ) : (
                    <p className="font-jamjuree font-bold text-lg w-fit">0</p>
                  ))}
                {project?.project_type === ProductsType.customer &&
                  (project?.processed_tickets ? (
                    <div className="flex items-center gap-1">
                      <div className="flex items-center">
                        <p className={'font-jamjuree font-bold text-lg w-fit'}>
                          {project?.processed_tickets}
                        </p>
                      </div>
                      {/*<div className="flex items-center">*/}
                      {/*  {project?.processed_tickets_diff > 0 && <ChevronUp />}*/}
                      {/*  {project?.processed_tickets_diff < 0 && <ChevronDown />}*/}
                      {/*  <p*/}
                      {/*    className={clsx(*/}
                      {/*      project?.processed_tickets_diff > 0 && 'text-[#389D61]',*/}
                      {/*      project?.processed_tickets_diff < 0 && 'text-[#C11A45]',*/}
                      {/*      'font-jamjuree font-semibold text-xs w-fit',*/}
                      {/*    )}*/}
                      {/*  >*/}
                      {/*    {project?.processed_tickets_diff}%*/}
                      {/*  </p>*/}
                      {/*</div>*/}
                    </div>
                  ) : (
                    <p className="font-jamjuree font-bold text-lg w-fit">0</p>
                  ))}
                {project?.project_type === ProductsType.bi && (
                  <p className="font-jamjuree font-bold text-lg w-fit">0</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
